export type DeviceHealthLevel = "healthy" | "warning" | "critical" | "unavailable";

export type DeviceHealthMetric = {
  level: DeviceHealthLevel;
  usedPercent?: number;
  freeBytes?: number;
  totalBytes?: number;
  levelPercent?: number;
  charging?: boolean;
  online?: boolean;
  latencyMs?: number;
  /** Battery only: the device's own estimate of time left on battery, in minutes. */
  minutesRemaining?: number;
  /** Battery only: observed discharge rate, percent per hour. */
  drainPercentPerHour?: number;
  /** Battery only: the OS low-power / battery-saver mode. */
  lowPowerMode?: boolean;
  /** Memory only: the OS memory-pressure state. Decides the memory level when present. */
  pressure?: "normal" | "warn" | "critical";
  /**
   * Disk and memory: bytes the device's space scan says it can reclaim
   * (recommended findings only). Aggregate only; no paths leave the device.
   */
  reclaimableBytes?: number;
  detail?: string;
};

export type DeviceHealthSnapshot = {
  schemaVersion: 1;
  deviceId: string;
  observedAt: number;
  platform: string;
  memory: DeviceHealthMetric;
  disk: DeviceHealthMetric;
  battery: DeviceHealthMetric;
  network: DeviceHealthMetric;
};

export type DeviceHealthIssue = {
  metric: "memory" | "disk" | "battery" | "network";
  level: "warning" | "critical";
  summary: string;
  recommendedAction: string;
};

export type DeviceHealthEvaluation = {
  level: "healthy" | "warning" | "critical";
  issues: DeviceHealthIssue[];
  unavailableMetrics: Array<"memory" | "disk" | "battery" | "network">;
};

const MAX_DEVICE_ID_LENGTH = 128;
const MAX_DETAIL_LENGTH = 240;
const MAX_CLOCK_SKEW_MS = 5 * 60 * 1000;
/**
 * How long a sample speaks for the device. An older one (a report replayed
 * after a reconnect) says nothing about now: it can neither raise, keep nor
 * clear an alert. Alerts about point-in-time conditions also expire this long
 * after the last sample that confirmed them.
 */
export const DEVICE_HEALTH_LIVE_READING_MS = 15 * 60 * 1000;
/**
 * How long a problem stays in the feed after the last sample that confirmed
 * it, so the alert of a device that stops reporting (asleep, offline, signed
 * out, retired) leaves by itself instead of lingering. Memory, battery and
 * connection readings are point-in-time; a full disk stays full while the
 * machine sleeps, so it keeps its alert across a weekend away. A row is
 * stored as long as its longest problem lasts, so no retention sweep takes an
 * alert that is still on; the feed shows each problem only while it lasts
 * ({@link lastingDeviceHealthRow}).
 */
export const DEVICE_HEALTH_ALERT_TTL_MS: Readonly<Record<DeviceHealthIssue["metric"], number>> = {
  memory: DEVICE_HEALTH_LIVE_READING_MS,
  battery: DEVICE_HEALTH_LIVE_READING_MS,
  network: DEVICE_HEALTH_LIVE_READING_MS,
  disk: 72 * 60 * 60 * 1000,
};
/**
 * Version of the device-health episode row. Rows from version 3 on expire by
 * `expiresAt` on the server clock; older ones carry a two-week expiry and
 * end by `legacyDeviceHealthRelevantUntil` instead.
 */
export const DEVICE_HEALTH_EPISODE_SCHEMA_VERSION = 3;

const objectRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;

const finiteTime = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/**
 * When a device-health row written before episode version 3 stops being
 * relevant (ms epoch), or null for every other row. Those rows expire two
 * weeks after their last write, so the alert of a device that stopped
 * reporting (or registered again under a new installation id, which no
 * sample of the old one will ever reach) stayed up for days. Read time ends
 * them as a version-3 row would end: one alert lifetime after the sample
 * that last confirmed them. A device that is still reporting rewrites its
 * row as version 3 on its next sample, keeping the row id and read state.
 */
export function legacyDeviceHealthRelevantUntil(row: Record<string, unknown>): number | null {
  if (row.source !== "device_health") return null;
  const categories = Array.isArray(row.categories) ? row.categories : [];
  if (categories.includes("battery_runway")) return null;
  const health = objectRecord(row.deviceHealth) ?? objectRecord(objectRecord(row.extraFields)?.deviceHealth);
  if (!health || health.batteryRunway !== undefined) return null;
  const version = finiteTime(health.schemaVersion);
  if (version !== null && version >= DEVICE_HEALTH_EPISODE_SCHEMA_VERSION) return null;
  const confirmedAt = finiteTime(health.observedAt) ?? finiteTime(row.storedDate);
  if (confirmedAt === null) return null;
  const lifetimes = categories
    .filter((entry): entry is DeviceHealthIssue["metric"] =>
      entry === "memory" || entry === "disk" || entry === "battery" || entry === "network")
    .map((metric) => DEVICE_HEALTH_ALERT_TTL_MS[metric]);
  return confirmedAt + (lifetimes.length > 0 ? Math.min(...lifetimes) : DEVICE_HEALTH_LIVE_READING_MS);
}
/** One week: the longest battery runway a device may report. */
export const MAX_BATTERY_MINUTES_REMAINING = 7 * 24 * 60;

function boundedString(value: unknown, maxLength: number): string {
  return typeof value === "string"
    ? value.trim().replace(/[\u0000-\u001f\u007f]/g, "").slice(0, maxLength)
    : "";
}

function finiteNumber(value: unknown, min: number, max: number): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value >= min && value <= max
    ? value
    : undefined;
}

function normalizeLevel(value: unknown): DeviceHealthLevel {
  return value === "healthy" || value === "warning" || value === "critical"
    ? value
    : "unavailable";
}

function normalizeMetric(value: unknown): DeviceHealthMetric {
  const metric = value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
  return {
    level: normalizeLevel(metric.level),
    ...(finiteNumber(metric.usedPercent, 0, 100) !== undefined
      ? { usedPercent: finiteNumber(metric.usedPercent, 0, 100) }
      : {}),
    ...(finiteNumber(metric.freeBytes, 0, Number.MAX_SAFE_INTEGER) !== undefined
      ? { freeBytes: finiteNumber(metric.freeBytes, 0, Number.MAX_SAFE_INTEGER) }
      : {}),
    ...(finiteNumber(metric.totalBytes, 1, Number.MAX_SAFE_INTEGER) !== undefined
      ? { totalBytes: finiteNumber(metric.totalBytes, 1, Number.MAX_SAFE_INTEGER) }
      : {}),
    ...(finiteNumber(metric.levelPercent, 0, 100) !== undefined
      ? { levelPercent: finiteNumber(metric.levelPercent, 0, 100) }
      : {}),
    ...(typeof metric.charging === "boolean" ? { charging: metric.charging } : {}),
    ...(typeof metric.online === "boolean" ? { online: metric.online } : {}),
    ...(finiteNumber(metric.latencyMs, 0, 120_000) !== undefined
      ? { latencyMs: finiteNumber(metric.latencyMs, 0, 120_000) }
      : {}),
    ...(metric.pressure === "normal" || metric.pressure === "warn" || metric.pressure === "critical"
      ? { pressure: metric.pressure }
      : {}),
    ...(finiteNumber(metric.reclaimableBytes, 0, Number.MAX_SAFE_INTEGER) !== undefined
      ? { reclaimableBytes: finiteNumber(metric.reclaimableBytes, 0, Number.MAX_SAFE_INTEGER) }
      : {}),
    ...(boundedString(metric.detail, MAX_DETAIL_LENGTH)
      ? { detail: boundedString(metric.detail, MAX_DETAIL_LENGTH) }
      : {}),
  };
}

/**
 * The network reading as the server can trust it. A report only arrives when
 * the device reached Smart Manager, so a reading that says it could not is
 * contradicted by its own delivery: the probe failed, timed out, or asked the
 * wrong place. It is kept as unavailable, never as "offline".
 */
function normalizeNetworkMetric(value: unknown): DeviceHealthMetric {
  const metric = normalizeMetric(value);
  if (metric.online !== false) return metric;
  return { level: "unavailable", detail: "The device's own reachability probe failed, but this report arrived." };
}

function normalizeBatteryMetric(value: unknown): DeviceHealthMetric {
  const metric = normalizeMetric(value);
  const raw = value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
  const minutesRemaining = finiteNumber(raw.minutesRemaining, 0, MAX_BATTERY_MINUTES_REMAINING);
  const drainPercentPerHour = finiteNumber(raw.drainPercentPerHour, 0, 100);
  return {
    ...metric,
    ...(minutesRemaining !== undefined ? { minutesRemaining } : {}),
    ...(drainPercentPerHour !== undefined ? { drainPercentPerHour } : {}),
    ...(typeof raw.lowPowerMode === "boolean" ? { lowPowerMode: raw.lowPowerMode } : {}),
  };
}

/**
 * Time left on battery in minutes: the device's own estimate, or one derived
 * from the charge level and the observed drain rate. Undefined when neither is
 * known. A device with this number is judged by the time-based battery runway.
 */
export function batteryMinutesRemaining(battery: DeviceHealthMetric): number | undefined {
  if (battery.minutesRemaining !== undefined) return battery.minutesRemaining;
  const { levelPercent, drainPercentPerHour } = battery;
  if (levelPercent === undefined || drainPercentPerHour === undefined || drainPercentPerHour < 0.5) {
    return undefined;
  }
  return Math.min(MAX_BATTERY_MINUTES_REMAINING, (levelPercent / drainPercentPerHour) * 60);
}

/**
 * When the sample was taken, on the server clock. A device stamps the sample
 * and, when it sends the report, the send time, both with its own clock: the
 * gap between the two is the sample's age whatever that clock's offset, so a
 * Mac whose clock runs behind still counts as live and a replayed sample
 * still counts as old. A report without a send time keeps the device's
 * timestamp, which may run at most MAX_CLOCK_SKEW_MS ahead of ours.
 */
function serverObservedAt(observedAt: unknown, sentAt: unknown, now: number): number | undefined {
  if (typeof observedAt !== "number" || !Number.isFinite(observedAt)) return undefined;
  const aligned = typeof sentAt === "number" && Number.isFinite(sentAt) && sentAt > 0
    ? now - Math.max(0, sentAt - observedAt)
    : observedAt;
  return finiteNumber(aligned, now - 24 * 60 * 60 * 1000, now + MAX_CLOCK_SKEW_MS);
}

export function parseDeviceHealthSnapshot(
  value: unknown,
  now = Date.now(),
  sentAt?: unknown,
): DeviceHealthSnapshot | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const deviceId = boundedString(record.deviceId, MAX_DEVICE_ID_LENGTH);
  const platform = boundedString(record.platform, 48);
  const observedAt = serverObservedAt(record.observedAt, sentAt, now);
  if (record.schemaVersion !== 1 || !deviceId || !platform || observedAt === undefined) {
    return null;
  }
  return {
    schemaVersion: 1,
    deviceId,
    observedAt,
    platform,
    memory: normalizeMetric(record.memory),
    disk: normalizeMetric(record.disk),
    battery: normalizeBatteryMetric(record.battery),
    network: normalizeNetworkMetric(record.network),
  };
}

/** Whether a sample is recent enough to speak for the device right now. */
export function isLiveDeviceHealthSample(snapshot: Pick<DeviceHealthSnapshot, "observedAt">, now: number): boolean {
  return now - snapshot.observedAt <= DEVICE_HEALTH_LIVE_READING_MS;
}

function issue(
  metric: DeviceHealthIssue["metric"],
  level: DeviceHealthIssue["level"],
  summary: string,
  recommendedAction: string,
): DeviceHealthIssue {
  return { metric, level, summary, recommendedAction };
}

/** What the user calls this machine, from the platform the device reports. */
export function deviceNoun(platform: string): "Mac" | "PC" | "computer" {
  const value = platform.trim().toLowerCase();
  if (value === "darwin" || value === "macos" || value === "mac" || value === "osx") return "Mac";
  if (value === "win32" || value === "windows" || value === "win") return "PC";
  return "computer";
}

/** Reclaimable amounts below this are too small to be worth offering. */
export const MIN_RECLAIM_MENTION_BYTES = 100 * 1000 ** 2;

/**
 * A size the way a person says it: whole gigabytes from 10 GB, one decimal
 * below that, and tens of megabytes under 1 GB. Decimal units, as the
 * Finder and Explorer storage panels show them.
 */
export function formatSpaceSize(bytes: number): string {
  const gb = bytes / 1000 ** 3;
  if (gb >= 9.95) return `${Math.round(gb)} GB`;
  if (gb >= 0.995) return `${Number(gb.toFixed(1))} GB`;
  return `${Math.max(10, Math.round(bytes / 1000 ** 2 / 10) * 10)} MB`;
}

function reclaimable(metric: DeviceHealthMetric): number | undefined {
  return metric.reclaimableBytes !== undefined && metric.reclaimableBytes >= MIN_RECLAIM_MENTION_BYTES
    ? metric.reclaimableBytes
    : undefined;
}

function memoryLevel(memory: DeviceHealthMetric): DeviceHealthIssue["level"] | null {
  // The OS pressure state is the truth when the device reports it: macOS keeps
  // memory "used" near 100% on purpose (cache and compression), so the
  // percentage alone would cry wolf on a machine that is coping fine.
  if (memory.pressure === "critical") return "critical";
  if (memory.pressure === "warn") return "warning";
  if (memory.pressure === "normal") return null;
  const used = memory.usedPercent;
  if (used !== undefined && used >= 95) return "critical";
  if (used !== undefined && used >= 85) return "warning";
  return null;
}

function memoryIssue(memory: DeviceHealthMetric, noun: string): DeviceHealthIssue | null {
  const level = memoryLevel(memory);
  if (!level) return null;
  const bytes = reclaimable(memory);
  const lead = level === "critical"
    ? `Your ${noun} is very low on memory and may slow down.`
    : `Your ${noun} is running low on memory.`;
  // States what is true, never an offer: the offer belongs to the row's
  // action, which only some surfaces can run (notifications/deviceSpace.ts).
  const summary = bytes === undefined
    ? lead
    : `${lead} Quitting apps you aren't using would free about ${formatSpaceSize(bytes)}.`;
  return issue("memory", level, summary, "Quit apps you aren't using to free memory.");
}

function diskIssue(disk: DeviceHealthMetric, noun: string): DeviceHealthIssue | null {
  const used = disk.usedPercent;
  const free = disk.freeBytes;
  const fiveGiB = 5 * 1024 ** 3;
  const fifteenGiB = 15 * 1024 ** 3;
  const level: DeviceHealthIssue["level"] | null =
    (used !== undefined && used >= 95) || (free !== undefined && free <= fiveGiB)
      ? "critical"
      : (used !== undefined && used >= 90) || (free !== undefined && free <= fifteenGiB)
        ? "warning"
        : null;
  if (!level) return null;
  const detail = used !== undefined
    ? ` (${Math.round(used)}% used)`
    : free !== undefined
      ? ` (${formatSpaceSize(free)} left)`
      : "";
  const lead = level === "critical"
    ? `Your ${noun} is nearly out of space${detail}.`
    : `Your ${noun} is almost full${detail}.`;
  const bytes = reclaimable(disk);
  const summary = bytes === undefined ? lead : `${lead} About ${formatSpaceSize(bytes)} can be cleared.`;
  return issue(
    "disk",
    level,
    summary,
    "Clear caches and old downloads, and empty the Trash, to make more space.",
  );
}

/**
 * The summary a settling device-health row is stored with once the problem
 * it raised has cleared. The feed never shows a settling row
 * (isSettlingDeviceHealthRow); a row's summary is required, and this one is
 * true wherever it is read.
 */
export function deviceRecoveredSummary(
  platform: string,
  metrics: readonly DeviceHealthIssue["metric"][],
): string {
  const noun = deviceNoun(platform);
  const distinct = new Set(metrics);
  if (distinct.size === 1 && distinct.has("disk")) return `Your ${noun} has room again.`;
  if (distinct.size === 1 && distinct.has("memory")) return `Your ${noun}'s memory is back to normal.`;
  if (distinct.size === 1 && distinct.has("battery")) return `Your ${noun}'s battery is fine again.`;
  if (distinct.size === 1 && distinct.has("network")) return `Your ${noun}'s connection is back to normal.`;
  return `Your ${noun} is back to normal.`;
}

/**
 * The feed fields a device alert takes from the problems it names. One rule
 * for the write (deviceHealthEpisodes `issueRow`) and for the read-time view
 * that drops problems that no longer last ({@link lastingDeviceHealthRow}),
 * so a row shown with fewer problems reads exactly as if it had been written
 * with them. Each line names the device and makes its own offer, so the row
 * is just those lines; there is no generic "your device needs attention" lead.
 */
export function deviceHealthAlertFields(issues: readonly DeviceHealthIssue[]) {
  const level: DeviceHealthIssue["level"] = issues.some((entry) => entry.level === "critical") ? "critical" : "warning";
  const critical = level === "critical";
  const summary = issues.map((entry) => entry.summary).join(" ");
  return {
    summary,
    summaryAudio: summary,
    presentationMode: critical ? "moment_alert" : "digest",
    surfacePriority: critical ? 98 : 82,
    urgency: critical ? 5 : 3,
    actionRequired: critical,
    categories: ["device_health", level, ...issues.map((entry) => entry.metric)],
    recommendedActions: issues.map((entry) => ({ metric: entry.metric, action: entry.recommendedAction, automatic: false })),
    level,
  };
}

const isIssue = (value: unknown): value is DeviceHealthIssue => {
  const entry = objectRecord(value);
  return entry !== null
    && (entry.metric === "memory" || entry.metric === "disk" || entry.metric === "battery" || entry.metric === "network")
    && (entry.level === "warning" || entry.level === "critical")
    && typeof entry.summary === "string"
    && typeof entry.recommendedAction === "string";
};

/** `record` with only the keys in `metrics`. */
function onlyMetrics(value: unknown, metrics: ReadonlySet<string>): Record<string, unknown> | undefined {
  const source = objectRecord(value);
  if (!source) return undefined;
  return Object.fromEntries(Object.entries(source).filter(([metric]) => metrics.has(metric)));
}

/**
 * Whether a device row is settling: its problem cleared and the server waits
 * to be sure it stays cleared before retiring the row. The row is stored so
 * a problem that comes back within the settle window continues it (same id,
 * a Done holds), but it is never shown. The alert leaving the feed already
 * says the problem is over, and whoever made space or quit apps knows it.
 */
export function isSettlingDeviceHealthRow(row: Record<string, unknown>): boolean {
  if (row.source !== "device_health") return false;
  const facts = objectRecord(row.deviceHealth) ?? objectRecord(objectRecord(row.extraFields)?.deviceHealth);
  return finiteTime(facts?.healthySince) !== null
    || (Array.isArray(row.categories) && row.categories.includes("recovered"));
}

/**
 * A device alert as it stands at `now`: only the problems that still last.
 *
 * An episode row is stored for as long as its longest problem lasts (a full
 * disk: days), so a retention sweep never deletes an alert that is still on
 * and its id, read and Done state survive a Mac asleep overnight. Each problem
 * says until when it lasts without another sample (`deviceHealth.lastingUntil`,
 * written by deviceHealthEpisodes). A memory or connection reading from
 * before the lid closed is over fifteen minutes later, so the feed drops its
 * line and re-derives the row's level, urgency, categories and offers from
 * what is left: the same rule the writer used. Nothing is written back: the
 * next sample rewrites the row. The row's identity and revision key
 * (`feedRevisionKey`, `peak`) are untouched, so a Done stays Done.
 *
 * Rows without `lastingUntil` (older rows, battery-runway and recovered rows)
 * pass through unchanged, as does a row whose every problem still lasts. A
 * row none of whose problems lasts is over (its expiry has passed) and is not
 * read; should one arrive, it passes through rather than being blanked.
 */
export function lastingDeviceHealthRow<T extends Record<string, unknown>>(row: T, now: number): T {
  if (row.source !== "device_health") return row;
  const lifted = objectRecord(row.deviceHealth);
  const extraFields = lifted ? null : objectRecord(row.extraFields);
  const facts = lifted ?? objectRecord(extraFields?.deviceHealth);
  if (!facts || !Array.isArray(facts.issues)) return row;
  const lasting = objectRecord(facts.lastingUntil);
  if (!lasting) return row;
  const issues = facts.issues.filter(isIssue);
  const kept = issues.filter((entry) => (finiteTime(lasting[entry.metric]) ?? 0) >= now);
  if (kept.length === 0 || kept.length === issues.length) return row;
  const metrics = new Set<string>(kept.map((entry) => entry.metric));
  const { level, ...fields } = deviceHealthAlertFields(kept);
  const nextFacts: Record<string, unknown> = {
    ...facts,
    level,
    issues: kept,
    ...(facts.reclaimableBytes !== undefined ? { reclaimableBytes: onlyMetrics(facts.reclaimableBytes, metrics) } : {}),
    ...(facts.autoOpenLevels !== undefined ? { autoOpenLevels: onlyMetrics(facts.autoOpenLevels, metrics) } : {}),
  };
  return {
    ...row,
    ...fields,
    ...(lifted ? { deviceHealth: nextFacts } : { extraFields: { ...extraFields, deviceHealth: nextFacts } }),
  };
}

/**
 * Deterministic device-health thresholds. Copy is written for the person who
 * owns the machine: it names it ("your Mac") and says what can be reclaimed.
 * It states facts and asks nothing: the offer to make more space is the row's
 * device action, shown only where a surface can run it, so a question in the
 * copy would be a dead end everywhere else. It never tells anyone to delete
 * anything.
 */
export function evaluateDeviceHealth(snapshot: DeviceHealthSnapshot): DeviceHealthEvaluation {
  const issues: DeviceHealthIssue[] = [];
  const noun = deviceNoun(snapshot.platform);
  const unavailableMetrics = (["memory", "disk", "battery", "network"] as const)
    .filter((metric) => snapshot[metric].level === "unavailable");
  const memory = memoryIssue(snapshot.memory, noun);
  if (memory) issues.push(memory);
  const disk = diskIssue(snapshot.disk, noun);
  if (disk) issues.push(disk);

  // A device that reports time left owns its battery alerts through the
  // time-based runway (batteryRunway.ts); the percentage rules stay silent so
  // the two never raise the same condition twice.
  const batteryLevel = batteryMinutesRemaining(snapshot.battery) === undefined
    ? snapshot.battery.levelPercent
    : undefined;
  if (snapshot.battery.charging === false && batteryLevel !== undefined && batteryLevel <= 10) {
    issues.push(issue(
      "battery",
      "critical",
      `Your ${noun}'s battery is at ${Math.round(batteryLevel)}%. Plug it in now.`,
      "Connect power before long-running work continues.",
    ));
  } else if (snapshot.battery.charging === false && batteryLevel !== undefined && batteryLevel <= 20) {
    issues.push(issue(
      "battery",
      "warning",
      `Your ${noun}'s battery is at ${Math.round(batteryLevel)}%. Plug it in soon.`,
      "Connect power soon or defer nonessential background work.",
    ));
  }

  // Delivered reports never carry `online: false` (parseDeviceHealthSnapshot
  // turns it into unavailable); this branch serves in-process callers and the
  // severity evaluation fixtures.
  if (snapshot.network.online === false) {
    issues.push(issue(
      "network",
      "critical",
      `Your ${noun} can't reach Smart Manager right now.`,
      "Keep sync work queued and retry it when connectivity returns.",
    ));
  } else if ((snapshot.network.latencyMs ?? 0) >= 1_500) {
    issues.push(issue(
      "network",
      "critical",
      `Your ${noun}'s connection is very slow (about ${Math.round(snapshot.network.latencyMs || 0)} ms).`,
      "Pause latency-sensitive transfers and retry them after the connection recovers.",
    ));
  } else if ((snapshot.network.latencyMs ?? 0) >= 500) {
    issues.push(issue(
      "network",
      "warning",
      `Your ${noun}'s connection is slow (about ${Math.round(snapshot.network.latencyMs || 0)} ms).`,
      "Expect slower synchronization and avoid starting duplicate transfers.",
    ));
  }

  return {
    level: issues.some((entry) => entry.level === "critical")
      ? "critical"
      : issues.length > 0
        ? "warning"
        : "healthy",
    issues,
    unavailableMetrics,
  };
}
